import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Calendar, Building, Filter, X } from 'lucide-react';
import { FilterState } from './StaffAllScannedItemsView';

interface StaffAllScannedItemsFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  suppliers: string[];
}

export const StaffAllScannedItemsFilters = ({ 
  filters, 
  onFiltersChange,
  suppliers 
}: StaffAllScannedItemsFiltersProps) => {
  const handleFilterChange = (key: keyof FilterState, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      searchQuery: '',
      selectedSupplier: '',
      dateRange: { start: '', end: '' },
      statusFilter: 'all'
    });
  };

  const hasActiveFilters = 
    filters.searchQuery || 
    filters.selectedSupplier || 
    filters.dateRange.start || 
    filters.dateRange.end || 
    filters.statusFilter !== 'all';

  return (
    <Card className="mb-4 md:mb-6">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-medium text-foreground">Filter & Search</h3>
          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearAllFilters}
              className="ml-auto"
            >
              <X className="w-4 h-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search items, barcodes, orders..."
              value={filters.searchQuery}
              onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Supplier Filter */}
          <div className="relative">
            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
            <Select 
              value={filters.selectedSupplier} 
              onValueChange={(value) => handleFilterChange('selectedSupplier', value === 'all' ? '' : value)}
            >
              <SelectTrigger className="pl-10">
                <SelectValue placeholder="All Suppliers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map(supplier => (
                  <SelectItem key={supplier} value={supplier}>
                    {supplier}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status Filter */}
          <Select 
            value={filters.statusFilter} 
            onValueChange={(value) => handleFilterChange('statusFilter', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New Products</SelectItem>
              <SelectItem value="existing">Existing in System</SelectItem>
              <SelectItem value="credit">Has Credit</SelectItem>
            </SelectContent>
          </Select>

          {/* Date Range - Start */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
            <Input
              type="date"
              placeholder="Start Date"
              value={filters.dateRange.start}
              onChange={(e) => handleFilterChange('dateRange', { 
                ...filters.dateRange, 
                start: e.target.value 
              })}
              className="pl-10"
            />
          </div>
        </div>

        {/* Second Row for End Date and Additional Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
          <div className="relative lg:col-start-4">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
            <Input
              type="date"
              placeholder="End Date"
              value={filters.dateRange.end}
              onChange={(e) => handleFilterChange('dateRange', { 
                ...filters.dateRange, 
                end: e.target.value 
              })}
              className="pl-10"
            />
          </div>
        </div>

        {/* Filter Summary */}
        {hasActiveFilters && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="text-sm text-muted-foreground mb-2">Active Filters:</div>
            <div className="flex flex-wrap gap-2">
              {filters.searchQuery && (
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                  Search: "{filters.searchQuery}"
                </span>
              )}
              {filters.selectedSupplier && (
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                  Supplier: {filters.selectedSupplier}
                </span>
              )}
              {filters.statusFilter !== 'all' && (
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                  Status: {filters.statusFilter === 'new' ? 'New Products' : 
                          filters.statusFilter === 'existing' ? 'Existing' : 
                          'Has Credit'}
                </span>
              )}
              {filters.dateRange.start && (
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                  From: {new Date(filters.dateRange.start).toLocaleDateString()}
                </span>
              )}
              {filters.dateRange.end && (
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                  To: {new Date(filters.dateRange.end).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};